<script setup lang="ts">
import type {
  TpgBlurEvent,
  TpgFocusEvent,
  TpgInputEvent,
} from '@tpg/grammar-web';
import { TextArea } from '@tpg/grammar-web';
import type { IconConstant } from '@tpg/grammar-web-icons';

import type { GrammarTextAreaType } from './component-types';

export interface GrammarTextAreaProps {
  value?: string;
  placeholder?: string;
  label?: string;
  counter?: number;
  type?: GrammarTextAreaType;
  message?: string;
  iconTemplate?: IconConstant | '';
  disabled?: boolean;
  readOnly?: boolean;
  rows?: number;
  formAssociated?: boolean;
  name?: string;
}

const props = withDefaults(defineProps<GrammarTextAreaProps>(), {
  value: '',
  placeholder: '',
  label: '',
  counter: 0,
  type: 'default',
  message: '',
  iconTemplate: '',
  disabled: false,
  readOnly: false,
  rows: 4,
  formAssociated: true,
  name: '',
});

const emit = defineEmits<{
  (event: 'blur', data: TpgBlurEvent): void;
  (event: 'change', data: TpgInputEvent<string>): void;
  (event: 'focus', data: TpgFocusEvent): void;
  (event: 'input', data: TpgInputEvent<string>): void;
  (event: 'update:value', value: string): void;
}>();

function onInput(event: TpgInputEvent<string>): void {
  const inputTarget = event.target;
  if (!(inputTarget instanceof TextArea)) {
    throw new TypeError('Expected a TextArea input event target.');
  }

  const value = inputTarget.value;
  if (value !== props.value) {
    emit('change', event);
  }

  emit('input', event);
  emit('update:value', value);
}

function onBlur(event: TpgBlurEvent): void {
  emit('blur', event);
}

function onFocus(event: TpgFocusEvent): void {
  emit('focus', event);
}
</script>

<template>
  <tpg-text-area
    v-bind="props"
    @tpg-input="onInput"
    @tpg-blur="onBlur"
    @tpg-focus="onFocus"
  />
</template>
